import type { AreaComponent } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { useStore } from 'zustand';
import { useEditAreaDraftStore } from '@/entities/edit-area';
import { DeleteButton } from '@/shared/ui';
import { applyEditAreaCommand } from '../model/apply-edit-area-command';
import { holeToolReasons } from '../model/hole-tool-reasons';
import { useRemoveArea } from '../model/use-remove-area';
import { ComponentForm } from './component-form';
import { PlaceOnFace } from './place-on-face';
import { PlacementItem } from './placement-item';

interface AreaInspectorProps {
  /** 펼친(= 고른) 인쇄 영역. */
  readonly component: AreaComponent;
}

/**
 * 인쇄 영역 줄을 펼치면 보이는 칸 — 고른 인쇄 영역의 이름·크기와 이 면 배치 정렬을 고친다(모양은 그릴 때 도구로 정한다).
 * 이 면에 없는 영역이면 배치 칸 자리에 [이 면에 배치]를 둔다(설계 §12-4).
 */
export const AreaInspector = ({ component }: AreaInspectorProps): ReactElement => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const faceKey = state.selection.face;
  const face = state.faces[faceKey];
  const placement = face?.placements.find((item) => item.componentId === component.id);
  // 배치할 표면은 고른 표면, 없으면 면의 유일한 표면이다.
  const surface =
    face?.surfaces.find((item) => item.id === state.selection.surfaceId) ??
    (face?.surfaces.length === 1 ? face.surfaces[0] : undefined);
  const otherFaces = state.draft.activeFaces.filter(
    (key) =>
      key !== faceKey &&
      (state.faces[key]?.placements.some((item) => item.componentId === component.id) ?? false),
  ).length;
  const disabled = state.locked || state.isUnsupported;
  // [인쇄 영역 삭제] — Delete 키와 같은 경로(useRemoveArea). 거부 사유는 제목 아래에 보인다.
  const removeArea = useRemoveArea();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // 인쇄 제외 영역 도구 — 이 영역의 구멍을 그리는 중이면 그 도구가 눌린다. 다시 누르면 그리기를 끝낸다.
  const drawing = state.shapeDrawing;
  const pressedHoleTool =
    drawing?.mode === 'hole' && drawing.componentId === component.id
      ? (drawing.holeTool ?? 'polygon')
      : null;
  const reasons = holeToolReasons({
    component,
    surface: face?.surfaces.find((item) => item.id === placement?.surfaceId),
    locked: disabled,
  });
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">모든 색상이 같은 인쇄 영역을 사용합니다.</p>
      {otherFaces > 0 ? (
        <p role="note" className="text-sm">{`다른 면 ${otherFaces}곳에도 적용됩니다.`}</p>
      ) : null}
      {deleteError === null ? null : (
        <p role="alert" className="text-sm text-destructive">
          {deleteError}
        </p>
      )}
      <ComponentForm
        key={`${state.generation}:${component.id}`}
        component={component}
        disabled={disabled}
        headerAction={
          <DeleteButton
            label="인쇄 영역 삭제"
            disabled={disabled}
            onClick={() => setDeleteError(removeArea(component.id))}
          />
        }
        onDirtyChange={(dirty) => state.setInputDirty('component', dirty)}
        onRemoveHole={(index) =>
          applyEditAreaCommand(store, () => state.removeShapeHole(component.id, index))
        }
        holeControl={{
          selectedIndex: state.selection.holeIndex,
          onSelect: (index) => state.setSelection({ componentId: component.id, holeIndex: index }),
          tools: {
            pressed: pressedHoleTool,
            rectReason: reasons.rect,
            polygonReason: reasons.polygon,
            onChoose: (tool) => {
              if (pressedHoleTool === tool) {
                state.endShapeDrawing();
                return;
              }
              state.beginShapeDrawing({
                componentId: component.id,
                name: component.name,
                mode: 'hole',
                holeTool: tool,
              });
            },
            onCancel: () => state.endShapeDrawing(),
          },
        }}
        onApply={(next) => applyEditAreaCommand(store, () => state.upsertComponent(next))}
      />
      {placement !== undefined ? (
        <PlacementItem placement={placement} />
      ) : (
        <PlaceOnFace
          key={component.id}
          component={component}
          surface={surface}
          disabled={disabled}
        />
      )}
    </div>
  );
};
